import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import {
  EMPTY_SHELL_STATE,
  ROOT_SUBTAB,
  decodeShellState,
  deliveryTabKey,
  encodeShellState,
  locationTabKey,
  normalizeShellState,
  type DeliverySubtab,
  type LocationFilters,
  type SearchTab,
  type ShellState,
} from '../model/shellState';

export const SEARCH_TABS_PARAM = 'shell';

export interface UseSearchTabs extends ShellState {
  openSearch: (key: string, label?: string) => void;
  // Takes the raw location id — an integer, exactly what the API returned — because callers hold
  // that and should not know the key format. `setLocationFilters` takes the tab key instead,
  // matching openDelivery/setActiveSubtab, because its caller already holds `search.key`.
  openLocation: (id: number, address: string, filters: LocationFilters) => void;
  setLocationFilters: (key: string, filters: LocationFilters) => void;
  closeSearch: (key: string) => void;
  setActiveSearch: (key: string) => void;
  // The delivery on a tab of its own, for the one that resolves to no titular the panel can key a
  // tab by. Takes the raw delivery id — the caller holds that and should not know the key format.
  openDeliveryTab: (id: string, label: string) => void;
  openDelivery: (key: string, id: string, label?: string) => void;
  // One operation for «open this «Usuario» AND land on this delivery of theirs». Composed here and
  // not by the caller: see the comment on the implementation.
  openDeliveryInNewSearch: (
    key: string,
    label: string | undefined,
    deliveryId: string,
    deliveryLabel: string | undefined,
    origin: DeliverySubtab['origin'],
  ) => void;
  // «Cambiar titular»: the parcel changed hands, so the «Entrega» leaves the tab it was read from
  // and lands on the new titular's. One operation for the same reason as the one above — see the
  // comment on the implementation.
  moveDeliveryToSearch: (
    fromKey: string,
    toKey: string,
    toLabel: string | undefined,
    deliveryId: string,
    deliveryLabel: string | undefined,
  ) => void;
  closeDelivery: (key: string, id: string) => void;
  setActiveSubtab: (key: string, subtab: string) => void;
}

function mapSearch(state: ShellState, key: string, fn: (s: SearchTab) => SearchTab): ShellState {
  return { ...state, searches: state.searches.map((s) => (s.key === key ? fn(s) : s)) };
}

export function useSearchTabs(): UseSearchTabs {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(
    () => decodeShellState(searchParams.get(SEARCH_TABS_PARAM)),
    [searchParams],
  );

  // Each commit reads the freshest state from the URL rather than a value
  // captured at render time, so a user's successive actions compose correctly.
  // The result is normalized before writing, keeping the persisted state
  // self-consistent (one action per event — the URL is the single source of truth).
  const commit = useCallback(
    (update: (prev: ShellState) => ShellState) => {
      setSearchParams((prevParams) => {
        const next = normalizeShellState(
          update(decodeShellState(prevParams.get(SEARCH_TABS_PARAM))),
        );
        const updated = new URLSearchParams(prevParams);
        if (next.searches.length === 0) updated.delete(SEARCH_TABS_PARAM);
        else updated.set(SEARCH_TABS_PARAM, encodeShellState(next));
        return updated;
      });
    },
    [setSearchParams],
  );

  const openSearch = useCallback(
    (key: string, label?: string) => {
      commit((prev) => {
        if (prev.searches.some((s) => s.key === key)) {
          // Re-open: activate the existing tab and refresh its label if a new one came in.
          const searches = prev.searches.map((s) =>
            s.key === key && label !== undefined ? { ...s, label } : s,
          );
          return { searches, activeKey: key };
        }
        const tab: SearchTab = {
          key,
          kind: 'user',
          ...(label !== undefined ? { label } : {}),
          deliveries: [],
          activeSubtab: ROOT_SUBTAB,
        };
        return { searches: [...prev.searches, tab], activeKey: key };
      });
    },
    [commit],
  );

  const openLocation = useCallback(
    (id: number, address: string, filters: LocationFilters) => {
      const key = locationTabKey(id);
      commit((prev) => {
        if (prev.searches.some((s) => s.key === key)) {
          // Re-open: the agent just ran a search, so land them on its result. Unlike openSearch,
          // that means returning to the root sub-tab — the filters ARE what this tab renders, and
          // a delivery left open would hide them. The delivery sub-tabs themselves stay.
          const searches = prev.searches.map((s) =>
            s.key === key ? { ...s, label: address, filters, activeSubtab: ROOT_SUBTAB } : s,
          );
          return { searches, activeKey: key };
        }
        const tab: SearchTab = {
          key,
          kind: 'location',
          label: address,
          filters,
          deliveries: [],
          activeSubtab: ROOT_SUBTAB,
        };
        return { searches: [...prev.searches, tab], activeKey: key };
      });
    },
    [commit],
  );

  const openDeliveryTab = useCallback(
    (id: string, label: string) => {
      const key = deliveryTabKey(id);
      commit((prev) => {
        // Re-open: activate the tab the agent already has. Nothing is refreshed — a delivery tab's
        // label IS the tracking that opened it, and the same tracking cannot name it differently.
        if (prev.searches.some((s) => s.key === key)) return { ...prev, activeKey: key };
        const tab: SearchTab = {
          key,
          kind: 'delivery',
          label,
          // No sub-tabs, and the root is where the detail renders: a delivery tab IS one delivery,
          // so there is nothing for a sub-tab strip to offer.
          deliveries: [],
          activeSubtab: ROOT_SUBTAB,
        };
        return { searches: [...prev.searches, tab], activeKey: key };
      });
    },
    [commit],
  );

  const setLocationFilters = useCallback(
    (key: string, filters: LocationFilters) => {
      commit((prev) => mapSearch(prev, key, (s) => ({ ...s, filters })));
    },
    [commit],
  );

  const closeSearch = useCallback(
    (key: string) => {
      commit((prev) => {
        const idx = prev.searches.findIndex((s) => s.key === key);
        if (idx === -1) return prev;
        const searches = prev.searches.filter((s) => s.key !== key);
        if (searches.length === 0) return EMPTY_SHELL_STATE;
        // When the active tab closes, fall to the tab that slid into its slot, else its left neighbour.
        const activeKey =
          prev.activeKey === key
            ? ((searches[idx] ?? searches[idx - 1])?.key ?? null)
            : prev.activeKey;
        return { searches, activeKey };
      });
    },
    [commit],
  );

  const setActiveSearch = useCallback(
    (key: string) => {
      commit((prev) => ({ ...prev, activeKey: key }));
    },
    [commit],
  );

  const openDelivery = useCallback(
    (key: string, id: string, label?: string) => {
      commit((prev) =>
        mapSearch(prev, key, (s) => ({
          ...s,
          deliveries: s.deliveries.some((d) => d.id === id)
            ? s.deliveries
            : [...s.deliveries, { id, ...(label !== undefined ? { label } : {}) }],
          activeSubtab: id,
        })),
      );
    },
    [commit],
  );

  // TWO facts, ONE commit, and it has to stay that way. `openSearch` followed by `openDelivery`
  // loses the second: `setSearchParams` does not compose two synchronous navigations in a single
  // tick, so the delivery would be written against a URL that does not know the tab yet. Composing
  // inside one `commit` is the only shape that survives — the same reason the slice's README states
  // «one state operation per event» as an invariant.
  const openDeliveryInNewSearch = useCallback(
    (
      key: string,
      label: string | undefined,
      deliveryId: string,
      deliveryLabel: string | undefined,
      origin: DeliverySubtab['origin'],
    ) => {
      const subtab: DeliverySubtab = {
        id: deliveryId,
        ...(deliveryLabel !== undefined ? { label: deliveryLabel } : {}),
        ...(origin !== undefined ? { origin } : {}),
      };
      commit((prev) => {
        const existing = prev.searches.find((s) => s.key === key);
        if (existing !== undefined) {
          // The «Usuario» is already on screen: join their deliveries rather than duplicating the
          // tab, and land on the one just searched.
          const searches = prev.searches.map((s) =>
            s.key === key
              ? {
                  ...s,
                  ...(label !== undefined ? { label } : {}),
                  deliveries: s.deliveries.some((d) => d.id === deliveryId)
                    ? s.deliveries
                    : [...s.deliveries, subtab],
                  activeSubtab: deliveryId,
                }
              : s,
          );
          return { searches, activeKey: key };
        }
        const tab: SearchTab = {
          key,
          kind: 'user',
          ...(label !== undefined ? { label } : {}),
          deliveries: [subtab],
          activeSubtab: deliveryId,
        };
        return { searches: [...prev.searches, tab], activeKey: key };
      });
    },
    [commit],
  );

  // THREE facts, ONE commit, and the reason is the one written above `openDeliveryInNewSearch`:
  // two `setSearchParams` in a single tick do not compose. A `closeDelivery` followed by an
  // `openDeliveryInNewSearch` would write the second against a URL that still carries the first's
  // state, and the agent would end up with the «Entrega» on both tabs or on neither.
  //
  // Why it moves at all: reassigning the titular is the one write that changes WHOSE the parcel is,
  // so leaving the «Entrega» under the previous titular would leave a sub-tab that contradicts the
  // record it is showing. The agent is sent where the parcel now lives, which is also where they
  // have to call — nobody notifies the new titular.
  const moveDeliveryToSearch = useCallback(
    (
      fromKey: string,
      toKey: string,
      toLabel: string | undefined,
      deliveryId: string,
      deliveryLabel: string | undefined,
    ) => {
      const subtab: DeliverySubtab = {
        id: deliveryId,
        ...(deliveryLabel !== undefined ? { label: deliveryLabel } : {}),
      };
      commit((prev) => {
        // Detach first, unconditionally — except from the destination itself. A parcel handed back
        // to the «Usuario» whose tab is already open would otherwise be removed by this step and
        // re-added by the next, which is correct by luck; doing it in one place keeps it correct on
        // purpose, and keeps the subtab's position stable instead of sending it to the end.
        //
        // A `delivery` tab is dropped whole rather than emptied: it holds no sub-tabs because the
        // TAB is that one delivery, so detaching the delivery would leave a tab with nothing behind
        // it, still labelled with the tracking of a parcel that now belongs on somebody's ficha.
        const detached = prev.searches
          .filter((tab) => !(tab.key === fromKey && tab.key !== toKey && tab.kind === 'delivery'))
          .map((tab) =>
            tab.key === fromKey && tab.key !== toKey
              ? {
                  ...tab,
                  deliveries: tab.deliveries.filter((d) => d.id !== deliveryId),
                  activeSubtab: tab.activeSubtab === deliveryId ? ROOT_SUBTAB : tab.activeSubtab,
                }
              : tab,
          );
        const destination = detached.find((tab) => tab.key === toKey);
        if (destination !== undefined) {
          const searches = detached.map((tab) =>
            tab.key === toKey
              ? {
                  ...tab,
                  ...(toLabel !== undefined ? { label: toLabel } : {}),
                  deliveries: tab.deliveries.some((d) => d.id === deliveryId)
                    ? tab.deliveries
                    : [...tab.deliveries, subtab],
                  activeSubtab: deliveryId,
                }
              : tab,
          );
          return { searches, activeKey: toKey };
        }
        const tab: SearchTab = {
          key: toKey,
          kind: 'user',
          ...(toLabel !== undefined ? { label: toLabel } : {}),
          deliveries: [subtab],
          activeSubtab: deliveryId,
        };
        return { searches: [...detached, tab], activeKey: toKey };
      });
    },
    [commit],
  );

  const closeDelivery = useCallback(
    (key: string, id: string) => {
      commit((prev) =>
        mapSearch(prev, key, (s) => ({
          ...s,
          deliveries: s.deliveries.filter((d) => d.id !== id),
          activeSubtab: s.activeSubtab === id ? ROOT_SUBTAB : s.activeSubtab,
        })),
      );
    },
    [commit],
  );

  const setActiveSubtab = useCallback(
    (key: string, subtab: string) => {
      commit((prev) => mapSearch(prev, key, (s) => ({ ...s, activeSubtab: subtab })));
    },
    [commit],
  );

  return {
    ...state,
    openSearch,
    openLocation,
    setLocationFilters,
    closeSearch,
    setActiveSearch,
    openDeliveryTab,
    openDelivery,
    openDeliveryInNewSearch,
    moveDeliveryToSearch,
    closeDelivery,
    setActiveSubtab,
  };
}
